import SettingsTabBase from "./SettingsTabBase.jsx";
import "../../widget/fields/ThemePicker.jsx";
import {getFontSize, setFontSize, userPreferences} from "../../../react/context/bryntumContext.jsx";
import {WIDGET_KEYS} from "../../../other/constants/WidgetKeys.jsx";
import {DEFAULT_THEME} from "../../../other/constants/TaskTypes.jsx";
import {SETTING_TAB_WIDGET_KEYS, ViewType} from "../../../other/constants/OtherKeys.jsx";
import { PERSISTENCE_STATE_KEYS } from "../../../other/constants/PersistenceStateKeys.jsx";

// There's an issue when changing the font size to the max, to fit the editor to browser window and then reducing the font size doesn't get editors
// back to their original width and height, this happens only when width and height of editor intersect and width and height of browser window.
export const resizeAllEditorsToOriginalSize = (editorHandler) => {
  editorHandler.getEditors().forEach((editor) => {
    if (editor.editorRef) {
      editor.editorRef.width = editor.editorRef.config.width;
      editor.editorRef.height = editor.editorRef.config.height;
    }
  })
}

// getting rows style in Board Settings
const getRowStyle = (settingRowStyle) => {
  switch(settingRowStyle) {
    case rowStyles.stripeFeature: {
      return [
        { name: "stripeFeature", value: true },
        { name: "gridlines", value: false }
      ]
    }
    case rowStyles.gridlines: {
      return [
        { name: "gridlines", value: true },
        { name: "stripeFeature", value: false },
      ]
    }
    case rowStyles.bothStripeAndGridlines: {
      return [
        { name: "stripeFeature", value: true },
        { name: "gridlines", value: true }
      ]
    }
    default:
      return;
  }

}

const rowStyles = {
  stripeFeature: "Stripe Rows",
  gridlines: "Show Gridlines",
  bothStripeAndGridlines: 'Stripe Rows & Show Gridlines'
}

const fontType = {
  regular: "Regular",
  heavy: "Heavy",
}

export default class SettingsBoardTab extends SettingsTabBase {
  static get $name() {
    return "SettingsBoardTab";
  }

  // Factoryable type name
  static get type() {
    return "settingsboardtab";
  }

  get swivuDeepLinkSlug() {
    return SETTING_TAB_WIDGET_KEYS.boardTab;
  }

  static get configurable() {
    return {
      title: "Board",
      cls: "p-[1em]",
      defaults: {
        cls: "flex-[1_0_100%] @md:flex-[1_0_50%]",
        labelWidth: "11em",
      },
      items: {
        boardThemeField: {
          type: WIDGET_KEYS.fieldThemePicker,
          name: "theme",
          label: "Board Theme",
          onPaint: "up.onLoadAppearanceSetting",
          onChange: "up.onUpdateAppearanceSetting",
        },
        boardFontField: {
          type: "combo",
          label: "Board Font",
          name: "fontType",
          items: Object.values(fontType),
          onPaint: "up.onLoadAppearanceSetting",
          onChange: "up.onUpdateAppearanceSetting",
        },
        boardFontSizeField: {
          type: "slider",
          name: "fontSize",
          text: "Board Font Size",
          min: 11,
          max: 25,
          cls: "flex-[1_0_100%] @md:flex-[1_0_50%] boardSettingFontSliderField",
          onPaint: "up.onLoadAppearanceSetting",
          onChange: "up.onUpdateAppearanceSetting",
        },
        boardRowHeightField: {
          label: "Board Row Height",
          name: "viewType",
          type: "combo",
          items: Object.values(ViewType),
          onPaint: "up.onLoadAppearanceSetting",
          onChange: "up.onUpdateAppearanceSetting",
        },
        boardRowsStyleField: {
          label: "Board Row Style",
          name: "rowStyle",
          type: "combo",
          items: Object.values(rowStyles),
          onPaint: "up.onLoadAppearanceSetting",
          onChange: "up.onUpdateAppearanceSetting",
        }
      }
    };
  }

  loadRecord(record, context) {
    super.loadRecord(record, context);
  }

  onLoadAppearanceSetting({ source }) {
    this.suspendEvents();
    switch (source.name) {
      case "theme": {
        let theme = userPreferences.getSetting("theme")
        if (!theme) {
          userPreferences.updateSetting("theme", DEFAULT_THEME);
          theme = DEFAULT_THEME;
        }
        source.value = theme;
        break;
      }
      case "fontType": source.value = userPreferences.getSetting("fontType") || fontType.regular; break;
      case "fontSize": source.value = getFontSize(); break;
      case "viewType": source.value = userPreferences.getSetting("viewType") || ViewType.normal; break;
      case "rowStyle": {
        if (userPreferences.getSetting("stripeFeature") === "true" && userPreferences.getSetting("gridlines") === "true") {
          source.value = rowStyles.bothStripeAndGridlines;
        } else if (userPreferences.getSetting("stripeFeature") === "true") {
          source.value = rowStyles.stripeFeature;
        } else {
          source.value = rowStyles.gridlines;
        }
        break;
      }
      default:
    }
    this.resumeEvents();
  }

  onUpdateAppearanceSetting({ source, userAction }) {
    if (userAction) {
      switch (source.name) {
        case "theme": {
          userPreferences.updateSetting("theme", source.value);
          /* When theme is switched to dark theme, disable the focus mode as focus mode is only supported for light theme */
          if (source.value === DEFAULT_THEME) userPreferences.updateSetting(PERSISTENCE_STATE_KEYS.taskBudgetFocusMode, String(false));
          break;
        }
        case "fontType": userPreferences.updateSetting("fontType", source.value); break;
        case "fontSize": {
          setFontSize({ value: source.value });
          userPreferences.updateSetting("fontSize", source.value);
          resizeAllEditorsToOriginalSize(this.editorHandler);
          break;
        }
        case "viewType": userPreferences.updateSetting("viewType", source.value); break;
        case "rowStyle": {
          const selectedRowStyle = getRowStyle(source.value);
          selectedRowStyle.forEach(selectedStyle => userPreferences.updateSetting(selectedStyle.name, String(selectedStyle.value)))
          break;
        }
      }
    }
  }

}

SettingsBoardTab.initClass();
